import React, { useState } from 'react';
import { UserCheck } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { buildImpersonationReturnPath, reloadToAccountHome, setImpersonationReturnPath } from './impersonationNav';

/**
 * Page admin : se connecter en tant qu'un autre utilisateur via email ou UID.
 * Réservée aux admins (montée derrière AdminRoute).
 */
export default function ImpersonatePage() {
  const { startImpersonation } = useAuth();
  const location = useLocation();
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = value.trim();
    if (!v) return;
    setErr('');
    setBusy(true);
    try {
      const payload = v.includes('@') ? { targetEmail: v } : { targetUid: v };
      await startImpersonation(payload);
      // Only remember the admin page after a successful start (no stale path on failure).
      setImpersonationReturnPath(buildImpersonationReturnPath(location));
      // Rechargement dur : démarre une session propre en tant que la cible.
      reloadToAccountHome();
    } catch (e2) {
      console.error('startImpersonation error:', e2);
      const code = e2?.code || '';
      const msg =
        code === 'functions/not-found'
          ? 'Utilisateur introuvable.'
          : code === 'functions/permission-denied'
            ? "Action non autorisée (admin requis ou cible interdite)."
            : e2?.message || "Échec de l'impersonation.";
      setErr(msg);
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto p-4 space-y-4">
      <div className="flex items-center gap-2">
        <UserCheck className="w-5 h-5 text-indigo-600" />
        <h1 className="text-lg font-semibold text-slate-900">Se connecter en tant que…</h1>
      </div>

      <p className="text-sm text-slate-600">
        Saisissez l'adresse e-mail ou l'UID de l'utilisateur. Vous serez connecté en tant que
        cet utilisateur jusqu'à ce que vous quittiez l'impersonation depuis la bannière.
      </p>

      <form onSubmit={handleSubmit} className="space-y-3">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="email@exemple.com ou UID Firebase"
          autoFocus
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        {err ? <div className="text-sm text-red-600">{err}</div> : null}
        <button
          type="submit"
          disabled={busy || !value.trim()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
        >
          <UserCheck className="w-4 h-4" />
          <span className="text-sm font-semibold">
            {busy ? 'Connexion…' : 'Se connecter en tant que cet utilisateur'}
          </span>
        </button>
      </form>
    </div>
  );
}
